import get from 'lodash.get';
import PropTypes from 'prop-types';
import { graphql } from 'react-relay';
import { defineMessages } from 'react-intl';
import { supportedAmpLocales } from 'dibs-intl/exports/constants';

export class PdpHead {
    constructor(props) {
        this.props = props;
        this.item = this.props.viewer.item;
        const isPdpUrlTranslationEnabled = this.props.viewer.isPdpUrlTranslationEnabled;
        this.pdpUrl = isPdpUrlTranslationEnabled ? this.item.localizedPdpUrl : this.item.pdpURL;
        this.intl = this.props.intl;
        this.baseUrl = this.props.intlVariables.localeDomain;
        this.canonicalUrl = `https://${this.baseUrl}${this.pdpUrl}`;
        this.msgs = defineMessages({
            siteName: {
                id: 'pdpHead.siteName',
                defaultMessage: '1stdibs.com',
            },
        });
        const media = get(props, 'viewer.item.photos[0].versions') || [];
        const image = media.find(p => get(p, 'type.name') === 'large') || {};
        this.imageUrl = image.webPath;
    }

    getCondensedPdpData() {
        return {
            title: this.title(),
            meta: this.meta(),
            link: [this.getCanonicalLink(), this.getDnsPrefetch()],
        };
    }

    getPdpData() {
        return {
            title: this.title(),
            meta: this.meta(),
            link: [...this.link()],
            script: [this.microdata()],
        };
    }

    getCanonicalLink() {
        return { rel: 'canonical', href: this.canonicalUrl };
    }

    getDnsPrefetch() {
        return { rel: 'dns-prefetch', href: this.props.clientConf.cdnDomain };
    }

    metaFacebook() {
        return [
            { name: 'fb:admins', content: this.props.clientConf.facebookAdmin },
            { name: 'fb:app_id', content: this.props.clientConf.facebookAppId },
            { name: 'fb:page_id', content: this.props.clientConf.facebookPageId },
        ];
    }

    metaProductPrice() {
        const { pdpMeta = {} } = this.item;

        if (!pdpMeta.productPriceAmount || !pdpMeta.productPriceCurrency) {
            return [];
        }

        return [
            { property: 'product:price:amount', content: pdpMeta.productPriceAmount },
            { property: 'product:price:currency', content: pdpMeta.productPriceCurrency },
        ];
    }

    metaTwitter() {
        const { pdpMeta = {} } = this.item;
        return [
            { name: 'twitter:card', content: 'summary' },
            { name: 'twitter:site', content: '@1stdibs' },
            { name: 'twitter:url', content: this.canonicalUrl },
            { name: 'twitter:title', content: pdpMeta.ogTitle },
            { name: 'twitter:description', content: pdpMeta.description },
            { name: 'twitter:image', content: this.imageUrl },
        ];
    }

    title() {
        const { pdpMeta = {} } = this.item;
        return pdpMeta.title;
    }

    metaRobots() {
        const { isListingNotListed, isMissingTranslatedItemTitle, isSold, metrics } = this.item;
        const noRecentPageViews = !Number(get(metrics, '[0].value'));

        // Add noindex if PDP is suspended or sold (without page views in the last 6 months) or missing translated item title.
        if (isListingNotListed || (isSold && noRecentPageViews) || isMissingTranslatedItemTitle) {
            return { name: 'robots', content: 'noindex' };
        }

        return {};
    }

    meta(metaMore = []) {
        return [
            this.metaDescription(),
            ...this.metaFacebook(),
            ...this.metaOg(),
            ...this.metaTwitter(),
            ...this.metaProductPrice(),
            this.metaRobots(),
            ...metaMore,
        ];
    }

    metaDescription() {
        const { pdpMeta = {} } = this.item;
        return { name: 'description', content: pdpMeta.description };
    }

    metaOg() {
        return [
            { name: 'og:site_name', content: this.intl.formatMessage(this.msgs.siteName) },
            { name: 'og:title', content: get(this.item, 'pdpMeta.ogTitle') },
            { name: 'og:url', content: this.canonicalUrl },
            { name: 'og:type', content: 'website' },
            { name: 'og:image', content: this.imageUrl },
        ];
    }

    link() {
        const { localeDomain, locale } = this.props.intlVariables;
        const { viewer } = this.props;
        const { item } = viewer;
        const { metrics, isSold } = item;

        const links = [this.getCanonicalLink(), this.getDnsPrefetch()];
        const isListingListed = !get(item, 'isListingNotListed') || get(item, 'seller.isInternal');

        //is not sold (without page views in the last 6 months)
        const isNotSold = !(isSold && !Number(get(metrics, '[0].value')));

        // if the current domain supports amp pages & the item is listed, include the amphtml link
        if (supportedAmpLocales.includes(locale) && isListingListed && isNotSold) {
            links.push({
                rel: 'amphtml',
                href: `https://${localeDomain}/amp${this.pdpUrl}`,
            });
        }

        return links;
    }

    microdata() {
        const { microdata } = this.item;
        return microdata
            ? {
                  type: 'application/ld+json',
                  innerHTML: microdata,
              }
            : {};
    }

    /**
     * @description Only use this with mobile. At some point DBL will handle preload links.
     */
    mobileOnlylinkPreload() {
        const fonts = [
            'MillerDisplay/MillerDisplay-Light.woff',
            'MillerDisplay/MillerDisplay-LightItalic.woff',
            'ProximaNova/ProximaNova-Light.woff',
            'ProximaNova/ProximaNova-Sbold.woff',
        ];
        return fonts.map(font => ({
            rel: 'preload',
            as: 'font',
            crossorigin: 'anonymous', // https://developer.mozilla.org/en-US/docs/Web/HTML/Preloading_content#Cross-origin_fetches
            href: `https://a.1stdibscdn.com/dist/fonts/${font}`,
        }));
    }
}

PdpHead.propTypes = {
    clientConf: PropTypes.object.isRequired,
    intlVariables: PropTypes.shape({
        locale: PropTypes.string,
        localeDomain: PropTypes.string,
    }).isRequired,
};

export const itemFragment = graphql`
    fragment PdpHead_viewer on Viewer {
        isPdpUrlTranslationEnabled: isEnabled(key: "translatedPdpUrls")
    }

    fragment PdpHead_item on Item {
        microdata(currency: $forcedCurrency)
        pdpURL
        localizedPdpUrl
        pdpMeta {
            title
            description
            ogTitle
            productPriceAmount
            productPriceCurrency
        }
        photos {
            versions {
                webPath
                type {
                    name
                }
            }
        }
        isListingNotListed
        isMissingTranslatedItemTitle
        seller {
            isInternal
        }
        isSold
        metrics(metricTypes: [LISTING_VIEWS], timeRange: LAST_6_MONTHS) {
            value
        }
    }
`;
